import { formatMoneyByDecimal, TFormattedMoney } from "@sembark-travel/money"
import { IDocGroup } from "../DocGroups"
import { ICountry } from "../Locations"
import { IUser } from "../Users"
import { TVisa, TVisaType } from "../Visa"
import { TTourist } from "../Tourists"
import { ISupplier, TSupplierContact } from "../Supplier"
import { IPaymentable } from "../Payments"
import { INewQuoteState, ITrip } from "../Trips"
import { TWorldCountry } from "../World"
import { isVisaRateExpired } from "../VisaServicePrices"
import { IDocType } from "../DocTypes"
import { IListResponse, XHRInstance } from "@sembark-travel/xhr"
import { IContact } from "../Contacts"
import { formatDate } from "@sembark-travel/datetime-utils"
import { isBookingPaymentSettled, TRejectedReason } from "./utils"
import { IAccount } from "../Accounting"

export type TVisaInBooking = {
	id?: number
	name?: string
	currency: string
	countries: ICountry[]
	trip_destinations?: ICountry[]
	document_groups?: IDocGroup[]
	visa_types: TVisaType[]
	visa_bookings: TVisaBooking[]
	created_at: string
	created_by: IUser
	updated_at?: string
	updated_by?: IUser
	deleted_at?: string
	deleted_by?: IUser
}

export interface TVisaBooking extends IPaymentable {
	id: number
	trip_id: number
	currency: string
	country?: TWorldCountry
	tourists: TTourist[]
	latest_booking_stage: TLatestBookingStage
	previous_visa_booking?: TVisaBooking
	document_groups: IDocGroup[]
	supplier?: ISupplier
	supplier_contact?: TSupplierContact
	booked_price: TFormattedMoney
	given_price: TFormattedMoney
	system_price: TFormattedMoney
	visa_service: TVisa
	visa_type: TVisaType
	visa_media_url?: string
	can_reinitiate: boolean
	can_view_prices?: boolean
	can_modify?: boolean
	is_shared?: boolean
	remarks: string | null
	comments: string | null
	debitable_accounts: Array<IAccount>
	created_at: string
	updated_at: string
}

export type TBookingLocation = {
	id: number
	location_id: number | null
	name: string
	short_name: string
	created_at: string
	updated_at: string
}

type TLatestBookingStage = {
	id: number
	booking_id: number
	title: string
	state: TBookingState
	description: null
	appointment_location?: TBookingLocation
	appointment_scheduled_at?: string
	comments?: string
	drop_reason?: string
	rejection_reason?: string
	reference_number?: string
	created_at?: string
	created_by?: IUser
	updated_at?: string
}

export type TBookingState =
	| "initialized"
	| "document_pending"
	| "document_received"
	| "document_verified"
	| "appointment_scheduled"
	| "appointment_done"
	| "in_progress"
	| "approved"
	| "rejected"
	| "dropped"

export type TVisaBookingStatus = "issued" | "rejected"

export interface ITripVisaBooking extends ITrip {
	visa_bookings: Array<TVisaBooking>
}

export type TBookingOptionType = {
	id: TBookingState
	name: string
}

type TAssignTouristInVisa = {
	visa_booking_id: number
	tourist_id: number | null
}

export type TReinitiate = {
	additional_documents: IDocType[]
	comments?: string
}

type TReinitiatePayload = {
	additional_required_document_types: number[]
	comments?: string
}

export type TAssignBulkGuestValues = {
	services: {
		id: number
		tripId: number
		name: string
		visa_bookings: {
			id: number
			visa_type: TVisaType
			tourist?: TTourist
		}[]
	}[]
}

export type TAssignSupplierToBulkServiceType = {
	stage: "select_booking" | "assign_supplier"
	supplier?: ISupplier
	supplier_contact?: TSupplierContact
	check: boolean
	services: {
		id: number
		tripId: number
		name: string
		check: boolean
		visa_bookings: {
			id: number
			currency: string
			visa_type: TVisaType
			country?: TWorldCountry
			given_price: TFormattedMoney
			booked_price: number
			tourist?: TTourist
			check: boolean
			remark?: string
			supplier?: ISupplier
			supplier_contact?: IContact
		}[]
	}[]
}

type AssignSupplierToBulkServicesPayload = {
	visa_supplier_id: number | null
	supplier_contact: number | null
	visa_bookings: {
		id: number
		currency: string
		booked_price: number
		remarks: string | null
	}[]
}

export type TVisaServiceTypeEdit = {
	service: TVisaInBooking
	booking: TVisaBooking
	document_groups: IDocGroup[]
	booked_price: number
	supplier?: ISupplier
	supplier_contact?: TSupplierContact
	remarks?: string
}

type TVisaServiceTypeEditPayload = {
	currency: string
	given_price: string | null
	booked_price: string | null
	supplier_id: number | null
	supplier_contact: number | null
	remarks: string | null
	document_groups:
		| {
				name: string
				document_types: number[]
		  }[]
		| null
}

export type TAddMoreVisaBookingFormValues = {
	visas: INewQuoteState["visas"]
	visasPrice: INewQuoteState["visasPrice"]
}

export type TBookingStage = {
	stage?: TBookingOptionType
	supplier?: ISupplier
	supplier_contact?: TSupplierContact
	scheduled_date?: string
	reference_number?: string
	rejection_reason?: TRejectedReason
	custom_rejection_reason?: string
	drop_reason?: string
	appointment_location?: string
	currency: string
	paid_amount?: number
	txn_currency?: string
	txn_amount?: number
	currency_to_txn_currency_exchange_rate?: number
	debit_account?: IAccount
	credit_account?: IAccount
	reference_id?: string
	file?: File
	url?: string
	comments?: string
}

export function XHR(xhr: XHRInstance) {
	return {
		async bulkAssignGuest(tripId: number, values: TAssignBulkGuestValues) {
			const payload: { data: TAssignTouristInVisa[] } = {
				data: values.services.flatMap(({ visa_bookings }) =>
					visa_bookings
						.map(({ id, tourist }) => ({
							visa_booking_id: id,
							tourist_id: tourist?.id ?? null,
						}))
						.filter(({ tourist_id }) => tourist_id !== null)
				),
			}

			return xhr
				.post<{
					data: { message: string }
				}>(`trip-visa-bookings/${tripId}/assign-tourists`, payload)
				.then((resp) => resp.data.data)
		},
		async assignSupplierToBulkVisaBookings(
			values: TAssignSupplierToBulkServiceType
		) {
			const { supplier, supplier_contact, services } = values
			const visaBookings = services.flatMap((s) =>
				s.visa_bookings.filter((vb) => vb.check)
			)
			const payload: AssignSupplierToBulkServicesPayload = {
				visa_supplier_id: supplier ? supplier.id : null,
				supplier_contact: supplier_contact ? supplier_contact.id : null,
				visa_bookings: visaBookings.map((s) => ({
					id: s.id,
					currency: s.currency,
					booked_price: s.booked_price,
					remarks: s.remark ?? null,
				})),
			}

			return xhr
				.post<{
					data: { message: string }
				}>(`visa-bookings/assign-service-providers`, payload)
				.then((resp) => resp.data.data)
		},
		async updateVisaBookingStage(booking: TVisaBooking, values: TBookingStage) {
			const payload = new FormData()
			payload.append("stage", values.stage?.id ?? "")
			if (values.supplier) {
				payload.append("visa_supplier_id", String(values.supplier?.id))
				if (values.supplier_contact) {
					payload.append(
						"supplier_contact",
						String(values.supplier_contact?.id)
					)
				}
			}
			if (values.stage?.id === "appointment_scheduled") {
				if (values.scheduled_date) {
					payload.append(
						"appointment_scheduled_at",
						values.scheduled_date
							? formatDate(values.scheduled_date, "YYYY-MM-DD HH:mm")
							: ""
					)
				}
				if (values.appointment_location) {
					payload.append(
						"appointment_location",
						values.appointment_location ?? ""
					)
				}
			}
			if (values.stage?.id === "approved") {
				if (values.reference_number) {
					payload.append("reference_number", values.reference_number ?? "")
				}
				if (values.file) {
					payload.append("file", values.file)
				}
			}
			if (values.stage?.id === "rejected") {
				payload.append(
					"rejection_reason",
					values.rejection_reason === "Others"
						? (values.custom_rejection_reason ?? "")
						: (values.rejection_reason ?? "")
				)
			}
			if (values.stage?.id === "dropped") {
				payload.append("drop_reason", values.drop_reason ?? "")
			}

			const isAllPaymentSettled = isBookingPaymentSettled(booking)

			if (values.stage?.id !== "document_verified" && !isAllPaymentSettled) {
				if (values.debit_account?.id) {
					payload.append(
						"debit_account_id",
						values.debit_account != null ? String(values.debit_account.id) : ""
					)
				}

				if (values.credit_account?.id && values.credit_account.id > 0) {
					payload.append("credit_account_id", String(values.credit_account.id))
				}

				if (values.paid_amount && values.paid_amount != null) {
					const paidAmount = Number(values.paid_amount)
					payload.append("paid_amount", String(paidAmount))
					payload.append("paid_amount_currency", values.currency)
					const isSameCurrency =
						!values.txn_currency || values.txn_currency === values.currency
					const txnCurrency = values.txn_currency || values.currency
					const txnAmount = isSameCurrency
						? paidAmount
						: values.txn_amount
							? Number(values.txn_amount)
							: 0

					payload.append("txn_amount", String(txnAmount))
					payload.append("txn_currency", txnCurrency)
				}

				payload.append("reference_id", values.reference_id ?? "")
			}

			payload.append("comments", values.comments ?? "")

			return xhr
				.patch<{
					data: TVisaBooking
				}>(`visa-booking/${booking.id}/active-stage`, payload)
				.then((resp) => resp.data.data)
		},
		async updateVisaTypeBooking(
			bookingId: number,
			values: TVisaServiceTypeEdit
		) {
			const payload: TVisaServiceTypeEditPayload = {
				booked_price: String(values.booked_price),
				given_price: formatMoneyByDecimal(values.booking.booked_price),
				currency: values.booking.currency,
				supplier_id: values.supplier ? values.supplier.id : null,
				supplier_contact: values.supplier_contact
					? values.supplier_contact.id
					: null,
				remarks: values.remarks ?? null,
				document_groups: values.document_groups.map((dg) => ({
					name: dg.name,
					document_types: dg.document_types.map((dt) => dt.id),
				})),
			}
			return xhr.put<{ data: TVisaBooking }>(
				`visa-bookings/${bookingId}`,
				payload
			)
		},
		async addMoreVisaBookings(
			tripId: number,
			values: TAddMoreVisaBookingFormValues,
			startDate: string
		) {
			const normalizeVisaServices = values.visas.services.map((service) => {
				const isExpired = isVisaRateExpired({
					rateValidityDate: service.visa_type?.price_valid_till,
					startDate,
				})
				let perQuantitySystemPrice: string | null = null
				if (!isExpired && service.visa_type?.cost_price) {
					perQuantitySystemPrice = formatMoneyByDecimal(
						service.visa_type.cost_price
					)
				}
				return {
					country_id: service.country?.id,
					visa_service_id: service.visa?.id,
					visa_type_id: service.visa_type?.id,
					pax: service.pax,
					currency: service.visa?.currency,
					per_quantity_system_price: perQuantitySystemPrice,
					per_quantity_booking_price: service.per_quantity_booked_price,
					comments: service.comments,
				}
			})

			const payload = {
				trip_id: tripId,
				visas: normalizeVisaServices.length > 0 ? normalizeVisaServices : [],
			}
			return xhr
				.post<{
					data: {
						message: string
						visa_bookings: TVisaBooking
					}
				}>(`visa-bookings`, payload)
				.then((resp) => resp.data.data)
		},
		async reinitiateVisaBooking(bookingId: number, values: TReinitiate) {
			const data: TReinitiatePayload = {
				additional_required_document_types: values.additional_documents.map(
					(doc) => doc.id
				),
				comments: values.comments?.trim() || undefined,
			}
			return xhr.post(`visa-bookings/${bookingId}/reinitiate`, data)
		},
		async getBookingLocations(
			params?: unknown
		): Promise<IListResponse<TBookingLocation>> {
			return xhr
				.get("/visa-bookings/appointment-locations", { params })
				.then((resp) => resp.data)
		},
		async getGuestWiseBookings(
			params: unknown
		): Promise<IListResponse<TVisaBooking & { trip: ITrip }>> {
			return xhr.get("/visa-bookings", { params }).then((resp) => resp.data)
		},
		async getTripWiseBookings(
			params?: unknown
		): Promise<IListResponse<ITripVisaBooking>> {
			return xhr
				.get("/trip-visa-bookings", { params })
				.then((resp) => resp.data)
		},
	}
}
